import Vue from "vue";
import AuthService from "@/services/AuthService";
import { DEFAULT_BRAND, bundledClients } from "./brandPresets";
import { extractBrandColors } from "./themeAdapter";

export const brandState = Vue.observable({ brand: DEFAULT_BRAND, brandsByKey: {} });

const CACHE_PREFIX = "hubblestar.branding.";

const inFlightByKey = {};

export async function loadClientBrand(clientId) {
  const brandKey = normalizeBrandKey(clientId);
  if (!brandKey) return DEFAULT_BRAND;

  const cachedBrandConfig = readCachedBrandConfig(brandKey);
  if (cachedBrandConfig) {
    return mergeBrand(brandKey, cachedBrandConfig);
  }

  if (!inFlightByKey[brandKey]) {
    inFlightByKey[brandKey] = fetchBrandOverrides(brandKey).finally(() => {
      delete inFlightByKey[brandKey];
    });
  }
  const brandOverrides = await inFlightByKey[brandKey];
  return mergeBrand(brandKey, brandOverrides);
}

async function fetchBrandOverrides(brandKey) {
  try {
    const sourceResponse = await AuthService.getSourceUrl(brandKey);
    const brandOverrides = mapBrandingConfig(sourceResponse?.data?.data?.brandingConfig);
    writeCachedBrandConfig(brandKey, brandOverrides);
    return brandOverrides;
  } catch {
    // Source-config fetch failed: fall back to the bundled brand (if any).
    return {};
  }
}

export function getClientBrand(clientId) {
  const brandKey = normalizeBrandKey(clientId);
  if (!brandKey) return DEFAULT_BRAND;

  if (!brandState.brandsByKey[brandKey]) {
    Vue.set(brandState.brandsByKey, brandKey, mergeBrand(brandKey, readCachedBrandConfig(brandKey)));
    loadClientBrand(clientId)
      .then((clientBrand) => Vue.set(brandState.brandsByKey, brandKey, clientBrand))
      .catch(() => {});
  }
  return brandState.brandsByKey[brandKey];
}


function mapBrandingConfig(brandingConfig) {
  const brandOverrides = {};
  if (!brandingConfig) return brandOverrides;
  if (brandingConfig.productName) brandOverrides.productName = brandingConfig.productName;
  if (brandingConfig.logoUrl) {
    brandOverrides.logo = {
      src: brandingConfig.logoUrl,
      width: brandingConfig.logoWidth || "56px",
    };
  }
  if (brandingConfig.faviconUrl) brandOverrides.favicon = brandingConfig.faviconUrl;
  const colors = extractBrandColors(brandingConfig);
  if (colors && Object.keys(colors).length) brandOverrides.colors = colors;
  return brandOverrides;
}

function readCachedBrandConfig(brandKey) {
  try {
    const cachedBrandConfig = sessionStorage.getItem(CACHE_PREFIX + brandKey);
    return cachedBrandConfig ? JSON.parse(cachedBrandConfig) : null;
  } catch {
    return null;
  }
}

function writeCachedBrandConfig(brandKey, brandOverrides) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + brandKey, JSON.stringify(brandOverrides));
  } catch {
    /* sessionStorage unavailable (private mode/quota) — branding refetches next load */
  }
}

// Resolve order: DEFAULT_BRAND <- bundled client <- DB override.
function mergeBrand(brandKey, brandOverrides) {
  const bundledBrand = bundledClients[brandKey];
  const hasOverrides = brandOverrides && Object.keys(brandOverrides).length > 0;
  if (!bundledBrand && !hasOverrides) {
    // Not a branded client (internal userTypes like "Teacher", or no config).
    return DEFAULT_BRAND;
  }
  return {
    ...DEFAULT_BRAND,
    ...bundledBrand,
    ...brandOverrides,
    colors: {
      ...DEFAULT_BRAND.colors,
      ...(bundledBrand && bundledBrand.colors),
      ...(brandOverrides && brandOverrides.colors),
    },
    key: brandKey,
  };
}

export function normalizeBrandKey(clientId) {
  return clientId ? String(clientId).toUpperCase() : null;
}
